// Rich task description: stored as a plain string with two simple tokens
// the view-mode renderer recognizes:
//
//   @Name                   → highlighted mention chip
//   ![filename](attachment:<id>)
//                           → inline image rendered via <AuthImage>
//
// Edit mode reuses MentionTextarea (for @-autocomplete) and adds image
// insertion via toolbar button, drag-drop, and clipboard paste. Each image
// uploads through api.uploadAttachment and gets inserted at the caret as
// `![filename](attachment:<id>)` markdown.
//
// We don't pretend to be a general markdown editor — this is a deliberately
// thin shell so the storage stays plain text (greppable, audit-friendly).
// View mode falls back to whitespace-preserved text for anything we don't
// specifically parse.

import { useState, useEffect, useRef, Fragment } from 'react';
import * as api from '../lib/api.ts';
import { AuthImage } from './AuthImage.jsx';
import { RichTextEditor } from './RichTextEditor.jsx';
import { isLegacy, legacyToHtml } from '../lib/richtext.ts';
import { sanitizeRichHtml, normalizeRichHtml } from '../lib/sanitize.ts';

const IMG_RE = /!\[([^\]]*)\]\(attachment:([a-f0-9]{8,64})\)/g;

/**
 * Render description text with inline images + highlighted mentions.
 * Returns React fragments. Whitespace + newlines preserved via CSS
 * (`white-space: pre-wrap`) on the wrapping element.
 */
function renderRichText(text, staff) {
  if (!text) return null;

  // Active staff names sorted longest-first to avoid prefix collisions
  // ("@Cherry" beats "@Cher"). Mirrors the worker's mention parser.
  const names = (staff || [])
    .filter(s => s.active && s.role !== 'system')
    .map(s => s.name)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  // Combined regex: image OR @mention.
  const namePart = names.length > 0
    ? names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')
    : null;
  const combined = new RegExp(
    `!\\[([^\\]]*)\\]\\(attachment:([a-f0-9]{8,64})\\)` +
    (namePart ? `|@(${namePart})(?=$|[\\s,.!?，。！？、:;()])` : ''),
    'gui'   // case-insensitive: render "@DEMI" / "@demi" as a chip for "Demi"
  );

  const parts = [];
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = combined.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push(<Fragment key={key++}>{text.slice(lastIdx, m.index)}</Fragment>);
    }
    if (m[2]) {
      // image
      const alt = m[1] || '';
      const id = m[2];
      parts.push(
        <span key={key++} className="rich-image-wrap">
          <AuthImage attachmentId={id} alt={alt} className="rich-image" />
        </span>
      );
    } else if (m[3]) {
      // @mention
      parts.push(
        <span key={key++} className="rich-mention">@{m[3]}</span>
      );
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    parts.push(<Fragment key={key++}>{text.slice(lastIdx)}</Fragment>);
  }
  return parts;
}

/**
 * Render stored rich content. Legacy token text (old rows) renders via
 * renderRichText; new sanitized HTML renders via dangerouslySetInnerHTML with
 * an authed inline-image swap (img[data-attachment-id] → blob URL). Shared by
 * the description view and the comment list.
 */
export function RichContent({ value, staff, className = '' }) {
  const ref = useRef(null);
  const legacy = isLegacy(value);
  const html = legacy ? '' : sanitizeRichHtml(value || '');

  useEffect(() => {
    if (legacy || !ref.current) return;
    const imgs = ref.current.querySelectorAll('img[data-attachment-id]');
    if (!imgs.length) return;
    let cancelled = false;
    const urls = [];
    imgs.forEach(img => {
      const id = img.getAttribute('data-attachment-id');
      if (!id) return;
      img.removeAttribute('src');           // avoid a broken-image flash
      api.fetchAttachmentBlobUrl(id).then(u => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        urls.push(u); img.src = u;
      }).catch(() => {});
    });
    return () => { cancelled = true; urls.forEach(u => URL.revokeObjectURL(u)); };
  }, [html, legacy]);

  if (legacy) {
    return <div className={`rich-content is-legacy ${className}`}>{renderRichText(value, staff)}</div>;
  }
  return <div className={`rich-content ${className}`} ref={ref} dangerouslySetInnerHTML={{ __html: html }} />;
}

export const RichDescriptionView = ({ value, staff, placeholder = '（点击添加描述）',
                                     onClick, className = '' }) => {
  const isEmpty = !value || !value.trim();
  return (
    <div className={`rich-desc-view ${isEmpty ? 'is-empty' : ''} ${className}`}
         role={onClick ? 'button' : undefined}
         tabIndex={onClick ? 0 : undefined}
         onClick={onClick}
         onKeyDown={(e) => onClick && (e.key === 'Enter' || e.key === ' ')
                            && (e.preventDefault(), onClick(e))}>
      {isEmpty ? <span className="rich-desc-placeholder">{placeholder}</span>
               : <RichContent value={value} staff={staff} />}
    </div>
  );
};

/**
 * Inline-editable rich description. View mode renders via RichDescriptionView;
 * clicking it opens the WYSIWYG RichTextEditor. Legacy token content is
 * converted to HTML on open (strangler-fig: it upgrades on the next save).
 */
export const RichDescriptionEditor = ({
  value, onSave, taskId, staff,
  placeholder = '（点击添加描述）'
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  function startEdit() {
    setDraft(isLegacy(value) ? legacyToHtml(value || '', staff) : (value || ''));
    setEditing(true);
  }

  async function save() {
    const next = normalizeRichHtml(draft);
    if (next === (value || '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(next || null);
      setEditing(false);
    } catch {
      // caller surfaces toast
    } finally {
      setSaving(false);
    }
  }

  function cancel() { setEditing(false); }

  if (!editing) {
    return (
      <RichDescriptionView value={value} staff={staff}
        placeholder={placeholder}
        onClick={startEdit}
        className="rich-desc-display" />
    );
  }

  return (
    <div className="rich-desc-editor">
      <RichTextEditor
        value={draft}
        onChange={setDraft}
        staff={staff}
        taskId={taskId}
        autoFocus
        rows={6}
        placeholder="描述待办 · 输入 @ 提到同事 · 粘贴或拖入图片"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void save(); }
          else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }} />
      <div className="rich-desc-toolbar">
        <span className="rich-desc-hint">⌘+Enter 保存 · Esc 取消</span>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn-ghost btn-sm"
                onClick={cancel} disabled={saving}>取消</button>
        <button type="button" className="btn btn-primary btn-sm"
                onClick={save} disabled={saving}>
          {saving ? '保存中…' : '保存'}
        </button>
      </div>
    </div>
  );
};
